import type { IConfig } from '../config/interfaces';

type AnalyticsPrimitive = string | number | boolean;
type AnalyticsValue = AnalyticsPrimitive | AnalyticsPrimitive[] | null | undefined;

export type WidgetAnalyticsParams = Record<string, AnalyticsValue>;

declare global {
	interface Window {
		dataLayer?: unknown[];
		gtag?: (...args: any[]) => void;
		__cobuyrConfiguredGoogleTagIds?: string[];
	}
}

const GOOGLE_TAG_SCRIPT_SELECTOR = 'script[data-cobuyr-google-tag="true"]';

const getGoogleTagIds = (): string[] => {
	return Array.from(
		new Set(
			[
				process.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID,
				process.env.REACT_APP_GOOGLE_TAG_ID,
				...((process.env.REACT_APP_GOOGLE_TAG_IDS || '')
					.split(',')
					.map((value) => value.trim())
					.filter(Boolean)),
			]
				.map((value) => (typeof value === 'string' ? value.trim() : ''))
				.filter(Boolean)
		)
	);
};

const ensureGoogleTagScript = (measurementId: string) => {
	if (typeof document === 'undefined') {
		return;
	}

	const existingScript =
		document.querySelector(GOOGLE_TAG_SCRIPT_SELECTOR) ||
		document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}"]`);

	if (existingScript) {
		return;
	}

	const script = document.createElement('script');
	script.async = true;
	script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
	script.setAttribute('data-cobuyr-google-tag', 'true');
	document.head.appendChild(script);
};

const ensureGtagBootstrap = () => {
	if (typeof window === 'undefined') {
		return;
	}

	window.dataLayer = window.dataLayer || [];

	if (typeof window.gtag !== 'function') {
		window.gtag = function gtag(...args: any[]) {
			window.dataLayer?.push(args);
		};
	}
};

const normalizeAnalyticsValue = (value: AnalyticsValue): AnalyticsPrimitive | undefined => {
	if (typeof value === 'string') {
		const trimmed = value.trim();
		return trimmed ? trimmed : undefined;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : undefined;
	}

	if (typeof value === 'boolean') {
		return value;
	}

	if (Array.isArray(value)) {
		const normalized = value
			.map((entry) => normalizeAnalyticsValue(entry))
			.filter((entry): entry is AnalyticsPrimitive => entry !== undefined);

		return normalized.length > 0 ? normalized.join(',') : undefined;
	}

	return undefined;
};

export const sanitizeAnalyticsParams = (
	params: WidgetAnalyticsParams = {}
): Record<string, AnalyticsPrimitive> => {
	return Object.entries(params).reduce<Record<string, AnalyticsPrimitive>>((accumulator, [key, value]) => {
		const normalizedValue = normalizeAnalyticsValue(value);

		if (normalizedValue !== undefined) {
			accumulator[key] = normalizedValue;
		}

		return accumulator;
	}, {});
};

const parseGoogleTagConfigParams = (): Record<string, AnalyticsPrimitive> => {
	const rawConfig = process.env.REACT_APP_GOOGLE_TAG_CONFIG_JSON;

	if (!rawConfig) {
		return {};
	}

	try {
		const parsed = JSON.parse(rawConfig);
		if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
			return {};
		}

		return sanitizeAnalyticsParams(parsed as WidgetAnalyticsParams);
	} catch (_error) {
		return {};
	}
};

const getGoogleSendPageView = (): boolean => {
	const rawValue = (process.env.REACT_APP_GOOGLE_TAG_SEND_PAGE_VIEW || '').trim().toLowerCase();

	if (!rawValue) {
		return false;
	}

	return rawValue === '1' || rawValue === 'true' || rawValue === 'yes';
};

export const buildWidgetAnalyticsContext = (
	config?: Pick<IConfig, 'source' | 'businessName' | 'bookingDetails' | 'order'>
): Record<string, AnalyticsPrimitive> => {
	const order = config?.order || [];
	const orderTotal = order.reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0);
	const orderQuantityTotal = order.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
	const productTypes = Array.from(new Set(order.map((item) => item.productType).filter(Boolean)));

	return sanitizeAnalyticsParams({
		widget_source: config?.source,
		business_name: config?.businessName,
		order_item_count: order.length,
		order_quantity_total: orderQuantityTotal,
		order_total: Number(orderTotal.toFixed(2)),
		product_types: productTypes,
		has_booking_details: Boolean(config?.bookingDetails),
		page_url: typeof window !== 'undefined' ? window.location.href : undefined,
		page_title: typeof window !== 'undefined' ? document.title : undefined,
	});
};

// Hash an email/identifier client-side so we don't ship raw PII into GA.
// Lightweight non-crypto hash — stable across calls for the same input.
const stableHash = (input: string): string => {
	let h = 2166136261;
	for (let i = 0; i < input.length; i += 1) {
		h ^= input.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return (h >>> 0).toString(36);
};

export interface WidgetIdentity {
	customerId?: string;
	customerEmail?: string;
}

export const setGtagIdentity = ({ customerId, customerEmail }: WidgetIdentity) => {
	if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
		return;
	}

	const trimmedId = (customerId || '').trim();
	const trimmedEmail = (customerEmail || '').trim().toLowerCase();
	const userId = trimmedId || (trimmedEmail ? stableHash(trimmedEmail) : '');

	if (!userId) {
		return;
	}

	window.gtag('set', { user_id: userId });
	window.gtag('set', 'user_properties', { cobuyr_customer_id: userId });
};

export const dispatchGtagEvent = (eventName: string, params: WidgetAnalyticsParams = {}) => {
	if (typeof window === 'undefined' || !eventName.trim()) {
		return;
	}

	const sanitizedParams = sanitizeAnalyticsParams(params);

	if (typeof window.gtag === 'function') {
		window.gtag('event', eventName, sanitizedParams);
		return;
	}

	if (Array.isArray(window.dataLayer)) {
		window.dataLayer.push({
			event: eventName,
			...sanitizedParams,
		});
	}
};

export const initializeGoogleAnalytics = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') {
		return;
	}

	const googleTagIds = getGoogleTagIds();

	if (googleTagIds.length === 0) {
		return;
	}

	ensureGtagBootstrap();
	ensureGoogleTagScript(googleTagIds[0]);

	const configParams = parseGoogleTagConfigParams();
	const sendPageView = getGoogleSendPageView();
	const configuredIds = new Set(window.__cobuyrConfiguredGoogleTagIds || []);

	window.gtag?.('js', new Date());

	for (const tagId of googleTagIds) {
		if (configuredIds.has(tagId)) {
			continue;
		}

		window.gtag?.('config', tagId, {
			send_page_view: sendPageView,
			page_location: window.location.href,
			page_title: document.title,
			...configParams,
		});
		configuredIds.add(tagId);
	}

	window.__cobuyrConfiguredGoogleTagIds = Array.from(configuredIds);
};

export const postParentAnalyticsEvent = (eventName: string, params: WidgetAnalyticsParams = {}) => {
	if (typeof window === 'undefined' || !eventName.trim()) {
		return;
	}

	const payload = JSON.stringify({
		action: 'track',
		event: eventName,
		params: sanitizeAnalyticsParams(params),
	});

	if (window.parent && window.parent !== window) {
		window.parent.postMessage(payload, '*');
		return;
	}

	dispatchGtagEvent(eventName, params);
};
